import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  Provider,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { provideRouter, TitleStrategy as BaseTitleStrategy, Router } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { routes } from './app.routes';
import { provideAnimations } from '@angular/platform-browser/animations';
import { NgxsModule } from '@ngxs/store';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { environment } from '../environments/environment';
import { RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha-2';
import { OnboardingState } from './store/onboarding/onboarding.state';
import { AuthApi, BoAuthModule } from '@bo/ng-auth';
import { AuthApiService } from './_services/auth-api-service/auth-api.service';
import { AppState } from './store/app/app.state';
import { ɵStateClass as StateClass } from '@ngxs/store/internals';
import { BoFormsModule, FieldPathDirective, FormFieldComponent } from '@bo/ng-forms';
import { NgxsStoragePluginModule, SESSION_STORAGE_ENGINE, LOCAL_STORAGE_ENGINE } from '@ngxs/storage-plugin';
import { BoUploadsModule } from '@bo/ng-uploads';
import { apiInterceptor } from './interceptors/http.interceptor';
import { AuthState } from './store/auth/auth.state';
import { TitleStrategy } from './_services/title-strategy/title-strategy.service';
import * as Sentry from '@sentry/angular';
import { PaystashioErrorHandler, SimpleErrorHandler } from './_services/error-handler.service';
import { BulkUploadState } from './store/bulk-uploads/bulk-upload.state';
import { provideMarkdown } from 'ngx-markdown';
import { enableSentry } from '../sentry';
import { NgxCurrencyInputMode, provideEnvironmentNgxCurrency } from '@dintecom/ngx-currency';
import { AccountingState } from './store/accounting/accounting.state';
import { TransactionsState } from './store/transactions/transactions.state';
import { LoginModalPaysComponent } from './shared/components/modals/login/login-modal.component';
import { IdVerificationState } from './store/id-verification/id-verification.state';
import { SubscriptionState } from './store/subscription/subscription.state';

//TODO: Re-look at a better solution of importing the states and provider without having to cast them.
const states = [
  OnboardingState,
  AppState,
  AuthState,
  BulkUploadState,
  AccountingState,
  TransactionsState,
  IdVerificationState,
  SubscriptionState,
] as StateClass[];
const externalProvider = [FieldPathDirective, FormFieldComponent, provideMarkdown()] as Provider[];

export const appConfig: ApplicationConfig = {
  providers: [
    ...externalProvider,
    {
      provide: BaseTitleStrategy,
      useClass: TitleStrategy,
    },
    provideRouter(routes),
    provideAnimations(), // needed for ngx-intl-tel-input
    provideHttpClient(withInterceptors([apiInterceptor])),
    importProvidersFrom(
      BsDropdownModule.forRoot(), // needed for ngx-intl-tel-input
      NgxsModule.forRoot(states, {
        developmentMode: !environment.production,
      }),
      NgxsStoragePluginModule.forRoot({
        keys: [
          { key: OnboardingState, engine: SESSION_STORAGE_ENGINE },
          { key: AppState, engine: SESSION_STORAGE_ENGINE },
          { key: AuthState, engine: LOCAL_STORAGE_ENGINE },
        ],
      }),
      NgxsReduxDevtoolsPluginModule.forRoot(),
      NgxsLoggerPluginModule.forRoot({
        disabled: environment.production,
      }),
      BoAuthModule.forRoot({
        loginUrl: '/login',
        loginModalComponent: LoginModalPaysComponent,
        jwtAuthHeaderPrefix: 'Bearer',
        apiRoot: environment.apiRoot,
      }),
      BoFormsModule.forRoot({
        indicateRequiredFields: true,
      }),
      BoUploadsModule.forRoot({
        apiBaseUrl: environment.apiRoot,
        apiInfoUrl: 's3',
        apiPrepUrl: 's3/prep',
        addTrailingSlash: true,
      }),
    ),
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.reCaptcha.siteKey },
    {
      provide: AuthApi,
      useClass: AuthApiService,
    },
    {
      provide: ErrorHandler,
      useClass: enableSentry ? PaystashioErrorHandler : SimpleErrorHandler,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      // Inject the TraceService first
      inject(Sentry.TraceService);

      // Instead of returning a function, we need to execute it immediately
      // or just return void
      return void (() => {})(); // or you could use: (() => {})();
    }),
    provideEnvironmentNgxCurrency({
      align: 'left',
      allowNegative: true,
      allowZero: true,
      decimal: '.',
      precision: 2,
      prefix: 'R ',
      suffix: '',
      thousands: ',',
      nullable: true,
      min: null,
      max: null,
      inputMode: NgxCurrencyInputMode.Financial,
    }),
  ],
};
